// Wrong-answer diff for the exercise: aligns the user's typed attempt with the correct
// spelling and renders a two-row, column-aligned character comparison (correct on top, the
// attempt below) so the exact miss — one wrong letter, a missing umlaut, a forgotten article —
// is visible at a glance instead of leaving the user to eyeball the two strings.
// Used by training_session.js at the answer reveal; pure string->html, no DOM access.
//
// Alignment is restricted Damerau-Levenshtein (unit costs, adjacent transposition) with a
// deterministic backtrace preference (match > transposition > substitution > deletion >
// insertion, scanning right-to-left) so the same miss always renders the same way.
var AnswerDiff = (function () {
	"use strict";

	function canon(s, caseSensitive) {
		return caseSensitive ? s : s.toLowerCase();
	}

	// Full DP matrix over the canonicalized strings, kept whole for the backtrace.
	// a = expected, b = attempt; d[i][j] = edits to turn a[0..i) into b[0..j).
	function buildMatrix(a, b) {
		var m = a.length, n = b.length;
		var d = new Array(m + 1);
		for (var i = 0; i <= m; i++) {
			d[i] = new Array(n + 1);
			d[i][0] = i;
		}
		for (var j = 0; j <= n; j++) {
			d[0][j] = j;
		}
		for (i = 1; i <= m; i++) {
			for (j = 1; j <= n; j++) {
				var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
				var v = Math.min(d[i - 1][j - 1] + cost, d[i - 1][j] + 1, d[i][j - 1] + 1);
				if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
					v = Math.min(v, d[i - 2][j - 2] + 1);//adjacent transposition: hte -> the is ONE slip
				}
				d[i][j] = v;
			}
		}
		return d;
	}

	function distance(expected, attempt, caseSensitive) {
		var a = canon(expected, caseSensitive), b = canon(attempt, caseSensitive);
		return buildMatrix(a, b)[a.length][b.length];
	}

	// The accepted form nearest to what was typed (first wins a tie) — diff against THAT, so a
	// user who typed "aerger" is compared with "Aerger", not shown ä-vs-ae noise against "Ärger".
	function closest(attempt, forms, caseSensitive) {
		var best = forms[0], bestDist = Infinity;
		for (var i = 0; i < forms.length; i++) {
			var dist = distance(forms[i], attempt, caseSensitive);
			if (dist < bestDist) {
				best = forms[i];
				bestDist = dist;
			}
		}
		return best;
	}

	// Backtrace into an op list over the ORIGINAL strings (the display keeps everyone's casing).
	// op.t: m match | x transposition (two chars per side) | s substitution |
	//       d deletion (char missing from the attempt) | i insertion (extra typed char).
	function backtrace(expected, attempt, caseSensitive) {
		var a = canon(expected, caseSensitive), b = canon(attempt, caseSensitive);
		var d = buildMatrix(a, b);
		var ops = [];
		var i = a.length, j = b.length;
		while (i > 0 || j > 0) {
			var v = d[i][j];
			if (i > 0 && j > 0 && a.charAt(i - 1) === b.charAt(j - 1) && v === d[i - 1][j - 1]) {
				ops.push({t: "m", e: expected.charAt(i - 1), a: attempt.charAt(j - 1)});
				i--; j--;
			} else if (i > 1 && j > 1 && v === d[i - 2][j - 2] + 1
					&& a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
				ops.push({t: "x", e: expected.substring(i - 2, i), a: attempt.substring(j - 2, j)});
				i -= 2; j -= 2;
			} else if (i > 0 && j > 0 && v === d[i - 1][j - 1] + 1) {
				ops.push({t: "s", e: expected.charAt(i - 1), a: attempt.charAt(j - 1)});
				i--; j--;
			} else if (i > 0 && v === d[i - 1][j] + 1) {
				ops.push({t: "d", e: expected.charAt(i - 1), a: null});
				i--;
			} else {//j > 0: insertion
				ops.push({t: "i", e: null, a: attempt.charAt(j - 1)});
				j--;
			}
		}
		return ops.reverse();
	}

	function escapeHtml(s) {
		return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	// One half of a column: chars from one row, or the · gap when this row has nothing there.
	function cell(text, cls) {
		var visible = text === null ? "·" : text;//spaces keep their width via white-space:pre (see views/exercise.css)
		return "<span class='" + cls + "'>" + escapeHtml(visible) + "</span>";
	}

	var OP_CLASS = {m: "adColM", x: "adColX", s: "adColS", d: "adColD", i: "adColI"};

	// One inline-flex column per aligned op — vertical alignment holds for any font.
	function renderHtml(expected, attempt, caseSensitive) {
		var ops = backtrace(expected, attempt, caseSensitive);
		var h = "<span class='adWrap'>";
		for (var k = 0; k < ops.length; k++) {
			var op = ops[k];
			h += "<span class='adCol " + OP_CLASS[op.t] + "'>" + cell(op.e, "adTop") + cell(op.a, "adBot") + "</span>";
		}
		return h + "</span>";
	}

	return {distance: distance, closest: closest, renderHtml: renderHtml};
})();
